import { FC, useState, useCallback } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import {
  InstallAgeFilter,
  type InstallAgeValue,
} from "@/components/filters/UserFilters/InstallAgeFilter/InstallAgeFilter";
import type { ScoringType, ProductInstallAgeBand } from "@/lib/scoring/types";
import { ProductScoringSection } from "./ProductScoringSection";

// ── Human-readable formatting ─────────────────────────────────────────────

function formatMonths(totalMonths: number): string {
  const years = Math.floor(totalMonths / 12);
  const months = totalMonths % 12;
  const parts: string[] = [];
  if (years > 0) parts.push(`${years} Year${years !== 1 ? "s" : ""}`);
  if (months > 0) parts.push(`${months} Month${months !== 1 ? "s" : ""}`);
  return parts.join(" ") || "0";
}

function getHumanInstallAge(min?: number, max?: number): string {
  if (min === undefined && max === undefined) return "—";
  if (min === undefined) return `up to ${formatMonths(max!)}`;
  if (max === undefined) return `greater than ${formatMonths(min)}`;
  return `${formatMonths(min)} – ${formatMonths(max)}`;
}

// ── Exports for criterion registry ────────────────────────────────────────

export const initialBand: ProductInstallAgeBand = {
  productIds: [],
  score: 0,
};

export function hasErrors(bands: unknown[]): boolean {
  const typed = bands as ProductInstallAgeBand[];
  if (typed.length === 0) return true;
  const productIds = typed[0]?.productIds ?? [];
  if (productIds.length === 0) return true;
  return typed.some((band, i) => {
    const others = typed.filter((_, j) => j !== i);
    return getBandError(band, others) !== null;
  });
}

export function getBandSummary(band: unknown): string {
  const b = band as ProductInstallAgeBand;
  return getHumanInstallAge(b.min, b.max);
}

// ── Validation ────────────────────────────────────────────────────────────

function getBandError(
  band: ProductInstallAgeBand,
  others: ProductInstallAgeBand[]
): string | null {
  if (!band.score) return "Points can't be empty";

  if (band.min === undefined && band.max === undefined) return "Range is empty";
  if (band.min !== undefined && band.max !== undefined && band.min > band.max)
    return "Min must be ≤ max";

  const rangeMin = band.min ?? -Infinity;
  const rangeMax = band.max ?? Infinity;
  if (
    others.some((o) => {
      const oMin = o.min ?? -Infinity;
      const oMax = o.max ?? Infinity;
      return rangeMin < oMax && oMin < rangeMax;
    })
  )
    return "Intervals intersect";

  return null;
}

// ── Component ─────────────────────────────────────────────────────────────

interface Props {
  criterion: ScoringType;
  onChange: (updated: ScoringType) => void;
}

export const ProductInstallAgeEditor: FC<Props> = ({ criterion, onChange }) => {
  const bands = criterion.productInstallAgeMonths ?? [];
  const [productIds, setProductIds] = useState<number[]>(
    bands[0]?.productIds ?? []
  );

  const handleProductsChange = useCallback(
    (ids: number[]) => {
      setProductIds(ids);
      onChange({
        ...criterion,
        productInstallAgeMonths: bands.map((b) => ({ ...b, productIds: ids })),
      });
    },
    [bands, criterion, onChange]
  );

  const updateBandAge = (index: number, ageValue: InstallAgeValue) => {
    const newBands = bands.map((b, i) =>
      i === index
        ? {
            ...b,
            productIds,
            min: ageValue.min ?? undefined,
            max: ageValue.max ?? undefined,
          }
        : { ...b, productIds }
    );
    onChange({ ...criterion, productInstallAgeMonths: newBands });
  };

  const updateBandScore = (index: number, score: number) => {
    const newBands = bands.map((b, i) =>
      i === index ? { ...b, score, productIds } : { ...b, productIds }
    );
    onChange({ ...criterion, productInstallAgeMonths: newBands });
  };

  const removeBand = (index: number) => {
    onChange({
      ...criterion,
      productInstallAgeMonths: bands
        .filter((_, i) => i !== index)
        .map((b) => ({ ...b, productIds })),
    });
  };

  const addBand = () => {
    onChange({
      ...criterion,
      productInstallAgeMonths: [
        ...bands.map((b) => ({ ...b, productIds })),
        { productIds, score: 0 },
      ],
    });
  };

  return (
    <Box>
      <ProductScoringSection
        productIds={productIds}
        onProductsChange={handleProductsChange}
      />

      {/* Header: Time Since First Detected */}
      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1 }}>
        <Typography variant="body2" fontWeight={600}>
          Time Since First Detected
        </Typography>
        <Tooltip
          title="The score will be applied if any of the entered products are found that match the specified age range"
          placement="top"
        >
          <HelpOutlineIcon sx={{ fontSize: 16, color: "text.secondary" }} />
        </Tooltip>
      </Stack>

      {/* CRITERIA / POINTS column headers */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
        }}
      >
        <Typography
          variant="overline"
          sx={{ fontWeight: 700, letterSpacing: 0.8 }}
        >
          Criteria
        </Typography>
        <Typography
          variant="overline"
          sx={{ fontWeight: 700, letterSpacing: 0.8 }}
        >
          Points
        </Typography>
      </Box>

      {/* Bands */}
      {bands.map((band, i) => {
        const others = bands.filter((_, j) => j !== i);
        const isDirty =
          band.min !== undefined || band.max !== undefined || band.score !== 0;
        const error = isDirty ? getBandError(band, others) : null;
        return (
          <Box
            key={i}
            sx={{
              border: "1px solid",
              borderColor: error ? "error.main" : "divider",
              borderRadius: 1,
              p: 1.5,
              mb: 1,
            }}
          >
            <Box
              sx={{
                "& .MuiFormControl-root": { flex: 1, minWidth: 0 },
              }}
            >
              <InstallAgeFilter
                value={{
                  min: band.min ?? null,
                  max: band.max ?? null,
                }}
                onChange={(val) => updateBandAge(i, val)}
              />
            </Box>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                gap: 1,
                mt: 1,
              }}
            >
              <TextField
                size="small"
                type="number"
                label="Pts"
                error={band.score === 0 && isDirty}
                value={band.score === 0 ? "" : band.score}
                onChange={(e) =>
                  updateBandScore(
                    i,
                    e.target.value === "" ? 0 : Number(e.target.value)
                  )
                }
                inputProps={{ min: 0, step: 1 }}
                sx={{ width: 80 }}
              />
              <IconButton
                size="small"
                onClick={() => removeBand(i)}
                aria-label="Remove band"
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Box>

            {error && (
              <Typography
                variant="caption"
                color="error"
                sx={{ display: "block", mt: 0.5 }}
              >
                {error}
              </Typography>
            )}
          </Box>
        );
      })}

      <Button
        size="small"
        startIcon={<AddIcon />}
        onClick={addBand}
        sx={{ color: "primary.main", fontWeight: 600 }}
      >
        ADD BAND
      </Button>
    </Box>
  );
};
